import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, type ThemeOptions } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FilterProvider } from './contexts/FilterContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import TenantForm from './pages/TenantForm';
import Users from './pages/Users';
import UserForm from './pages/UserForm';
import UserPermissions from './pages/UserPermissions';
import Servers from './pages/Servers';
import ServerForm from './pages/ServerForm';
import Agents from './pages/Agents';
import AgentForm from './pages/AgentForm';
import ApplicationDefinitions from './pages/ApplicationDefinitions';
import HealthCheckHistory from './pages/HealthCheckHistory';
import ServerMetrics from './pages/ServerMetrics';
import Alerts from './pages/Alerts';
import SonIslemZamani from './pages/SonIslemZamani';
import Bankalar from './pages/Bankalar';
import BankaForm from './pages/BankaForm';
import BankaServisTetikleme from './pages/BankaServisTetikleme';
import Sirketler from './pages/Sirketler';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <PrivateRoute>
            <Tenants />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants/new"
        element={
          <PrivateRoute>
            <TenantForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants/:id/edit"
        element={
          <PrivateRoute>
            <TenantForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute>
            <Users />
          </PrivateRoute>
        }
      />
      <Route
        path="/users/new"
        element={
          <PrivateRoute>
            <UserForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/users/:id/edit"
        element={
          <PrivateRoute>
            <UserForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/users/:id/permissions"
        element={
          <PrivateRoute>
            <UserPermissions />
          </PrivateRoute>
        }
      />
      <Route
        path="/servers"
        element={
          <PrivateRoute>
            <Servers />
          </PrivateRoute>
        }
      />
      <Route
        path="/servers/new"
        element={
          <PrivateRoute>
            <ServerForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/servers/:id/edit"
        element={
          <PrivateRoute>
            <ServerForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <PrivateRoute>
            <Agents />
          </PrivateRoute>
        }
      />
      <Route
        path="/agents/new"
        element={
          <PrivateRoute>
            <AgentForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/agents/:id/edit"
        element={
          <PrivateRoute>
            <AgentForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/application-definitions"
        element={
          <PrivateRoute>
            <ApplicationDefinitions />
          </PrivateRoute>
        }
      />
      <Route
        path="/application-definitions/:id/history"
        element={
          <PrivateRoute>
            <HealthCheckHistory />
          </PrivateRoute>
        }
      />
      <Route
        path="/metrics/servers/:serverId"
        element={
          <PrivateRoute>
            <ServerMetrics />
          </PrivateRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <PrivateRoute>
            <Alerts />
          </PrivateRoute>
        }
      />
      <Route
        path="/estatement/son-islem-zamani"
        element={
          <PrivateRoute>
            <SonIslemZamani />
          </PrivateRoute>
        }
      />
      <Route
        path="/estatement/sirketler"
        element={
          <PrivateRoute>
            <Sirketler />
          </PrivateRoute>
        }
      />
      <Route
        path="/estatement/bankalar"
        element={
          <PrivateRoute>
            <Bankalar />
          </PrivateRoute>
        }
      />
      <Route
        path="/estatement/bankalar/new"
        element={
          <PrivateRoute>
            <BankaForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/estatement/bankalar/:id/edit"
        element={
          <PrivateRoute>
            <BankaForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/estatement/banka-servis-tetikleme"
        element={
          <PrivateRoute>
            <BankaServisTetikleme />
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

const getThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: mode === 'light' ? '#f8fafc' : '#0f172a',
      paper: mode === 'light' ? '#ffffff' : '#1e293b',
    },
    text: {
      primary: mode === 'light' ? '#1e293b' : '#f1f5f9',
      secondary: mode === 'light' ? '#64748b' : '#94a3b8',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
  ],
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08), 0 4px 12px -2px rgba(0,0,0,0.06)',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          overflow: 'hidden',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
    // Select ve Autocomplete tek tip görünüm; dropdown'da tek scroll
    MuiSelect: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: 320,
              overflow: 'hidden',
              boxShadow: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mt: 0.5,
            },
          },
          MenuListProps: {
            sx: {
              maxHeight: 320,
              overflowY: 'auto',
              py: 0,
            },
          },
        },
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
        slotProps: {
          paper: {
            sx: {
              overflow: 'hidden',
              boxShadow: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mt: 0.5,
              bgcolor: 'background.paper',
            },
          },
        },
        ListboxProps: {
          sx: {
            maxHeight: 320,
            overflowY: 'auto',
            py: 0,
          },
        },
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

const AppWithTheme = () => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => createTheme(getThemeOptions(mode)), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <FilterProvider>
            <AppRoutes />
          </FilterProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <AppWithTheme />
    </ThemeModeProvider>
  );
}

export default App;
